import { SxProps } from '@mui/material';
import {
  badgeYellow,
  borderLight,
  grayDefault,
  grey9,
  surfaceGrey,
  textPrimary,
  textSecondary,
  white,
  yellow,
  yellow1,
} from 'app/components/ThemeProvider/colors';
import { getTheme } from 'app/components/ThemeProvider/theme';

const theme = getTheme();

export const header: SxProps = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '24px',
  height: '80px',
  p: '12px 32px',
  background: white,
  borderBottom: `1px solid ${borderLight}`,

  [theme.breakpoints.down('md')]: {
    height: '68px',
    p: '12px 16px',
    gap: '16px',
  },
};

export const logoSide: SxProps = {
  display: 'flex',
  alignItems: 'center',
  gap: '4px',

  '& a': {
    display: 'flex',
    lineHeight: 0,
  },
};

export const logo: SxProps = {
  width: '134px',
  height: '49px',

  [theme.breakpoints.down('md')]: {
    width: '94px',
    height: '35px',
  },
};

export const notif: SxProps = {
  background: yellow1,
  display: 'flex',
  borderRadius: '30px',
  justifyContent: 'center',
  alignItems: 'center',
  width: 'fit-content',
  height: '20px',
  p: '3px 8px 0 8px',
};

export const notifText: SxProps = {
  fontSize: '12px',
  fontWeight: '700',
  fontFamily: 'Yelo',
  lineHeight: 1,
};

/** Mobile drawer: full height, column layout — header on top, sidebar below */
export const drawerContainer: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: '100%',
  background: white,
};

/** Drawer header — logo on the left, close button on the right (Figma) */
export const drawerHeader: SxProps = {
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '16px',
  flex: 'none',
  width: '100%',
  height: '68px',
  p: '12px 16px',
  background: white,
  borderBottom: `1px solid ${borderLight}`,
};

export const drawerCloseButton: SxProps = {
  flex: 'none',
  p: '16px',
  borderRadius: '10px',

  '& .MuiSvgIcon-root': {
    width: '14px',
    height: '14px',
  },
};

export const infoSide: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '24px',
  height: '56px',

  [theme.breakpoints.down('md')]: {
    gap: '20px',
  },
};

export const notificationButton: SxProps = {
  position: 'relative',
  flex: 'none',
  width: '56px',
  height: '56px',
  p: '16px',
  borderRadius: '10px',
  background: white,

  [theme.breakpoints.down('md')]: {
    width: '24px',
    height: '24px',
    p: 0,

    '& .MuiSvgIcon-root': {
      width: '24px',
      height: '24px',
    },
  },
};

/** Figma: 8x8 dot + 3px white ring, 32/17px from the button's top-left */
export const notificationBadge: SxProps = {
  position: 'absolute',
  boxSizing: 'content-box',
  width: '8px',
  height: '8px',
  left: '29px',
  top: '15px',
  background: badgeYellow,
  border: `3px solid ${white}`,
  borderRadius: '24px',

  // On mobile the button is a 24x24 icon (no padding) — badge sits at the icon's top-right
  [theme.breakpoints.down('md')]: {
    left: '13px',
    top: '-3px',
  },
};

export const profileBox: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '12px',
  height: '48px',
  minWidth: 0,
  p: 0,
  textTransform: 'none',
  background: 'transparent',

  '&:hover': {
    background: 'transparent',
  },
};

export const avatar: SxProps = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flex: 'none',
  width: '48px',
  height: '48px',
  background: surfaceGrey,
  borderRadius: '100px',
  fontFamily: 'Yelo',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  color: textSecondary,
};

export const profileText: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: '2px',
  minWidth: 0,

  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
};

export const userName: SxProps = {
  maxWidth: '181px',
  fontFamily: 'Yelo',
  fontSize: '14px',
  fontWeight: 500,
  lineHeight: '24px',
  textTransform: 'uppercase',
  color: textPrimary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const userSignType: SxProps = {
  maxWidth: '181px',
  fontFamily: 'Yelo',
  fontSize: '14px',
  fontWeight: 400,
  lineHeight: '20px',
  color: textSecondary,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

export const notificationOverlay: SxProps = {
  position: 'fixed',
  top: '80px',
  right: 0,
  height: '100vh',
  width: { xs: '100vw', sm: 480 },
  bgcolor: 'white',
  zIndex: 1300,
  boxShadow: 6,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  borderLeft: `1px solid ${grayDefault}`,
};

export const notificationHeader: SxProps = {
  display: 'flex',
  alignItems: 'center',
  p: '32px',
  borderBottom: `1px solid ${grey9}`,
  position: 'fixed',
  width: '100%',
  backgroundColor: white,
  zIndex: 9,
  fontSize: 24,
  fontWeight: 600,
};

export const notificationTitle: SxProps = {
  fontWeight: 700,
  fontSize: 20,
};

export const notificationDot: SxProps = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  backgroundColor: yellow,
  position: 'absolute',
  left: 16,
};

export const burgerButton: SxProps = {
  backgroundColor: 'transparent',
  p: '16px !important',
  minWidth: '0px',
  border: 'none',

  '&:hover': {
    backgroundColor: 'transparent',
    border: 'none',
  },
};
